import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { AUTH_URL } from 'src/configs';
import request from 'src/request';

interface BankAccountsState {
  data: {
    bankAccounts: object[];
    totalCount: number;
    totalPages: number;
  };
  loading: boolean;
  error: string | null;
}

const initialState: BankAccountsState = {
  data: {
    bankAccounts: [],
    totalCount: 0,
    totalPages: 0,
  },
  loading: false,
  error: null,
};

// The endpoint takes page/limit/sort/order and nothing else. It used to be sent
// `field` and `value` too, which the API's query schema rejects outright — so
// every search typed here came back 400 and emptied the table.
export const fetchBankAccounts = createAsyncThunk(
  'components/fetchBankAccounts',
  async ({ page, sort, order }: { page: number; sort?: string; order?: string }) => {
    const params = new URLSearchParams({ page: String(page), limit: '10' });

    if (sort) params.set('sort', sort);
    if (order) params.set('order', order);

    const response = await request.get(
      `${AUTH_URL}/v1/admin/bank-accounts?${params.toString()}`
    );
    return {
      data: response.data.bank_accounts,
      totalCount: response.data.meta.total_count,
      totalPages: response.data.meta.page_count,
      page,
    };
  }
);

const bankAccountsData = createSlice({
  name: 'bank-accounts',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBankAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBankAccounts.fulfilled, (state, action) => {
        state.data = {
          bankAccounts: action.payload.data,
          totalCount: action.payload.totalCount,
          totalPages: action.payload.totalPages,
        };
        state.loading = false;
      })
      .addCase(fetchBankAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch bank accounts';
      });
  },
});

export default bankAccountsData.reducer;
